/**
 * manifestoConfig.js
 *
 * Background choreography for the hero manifesto ritual (the 2s hold-to-activate). Parallel to
 * transitionConfig.js / postProcessConfig.js — all timing/easing/look values for the manifesto live here
 * so they can be tuned in one place.
 *
 *   Hold (progress 0→1)  the form FREEZES (motion eases to a standstill) and SHAKES (amplitude ramps in).
 *                        Driven frame-accurately by HomeHero's hold progress via ShimmeringPrism.setHold().
 *   Completion           the form EXPLODES to MANIFESTO_LOOK (the central sphere appears at prismSize 6),
 *                        un-freezing and stopping the shake — ShimmeringPrism.setManifestoActive(true).
 *   Deactivation         returns to the active section preset — setManifestoActive(false).
 *
 * Durations are seconds; eases are GSAP ease names.
 */

/**
 * The manifesto "explode" look. ONLY these keys change; every other param stays at its section default
 * (per design). prismSize 0→6 makes the central sphere appear; camRotSpeed 0 stops the autonomous orbit;
 * mouseRotAmp 0.5 makes it strongly mouse-reactive; timeSpeed 0.2 churns the form fast — and the voice
 * volume adds on top in _animate (timeSpeed = 0.2 + voiceLevel × MANIFESTO_TIMESPEED_AUDIO_GAIN).
 */
export const MANIFESTO_LOOK = {
    shininess: 0.35,
    baseOverture: 8.5,
    prismSize: 6,
    timeSpeed: 0.1,
    cameraDist: 40,
    camRotSpeedX: 0,
    camRotSpeedY: 0,
    mouseRotAmp: 0.5,
};

/** Voice-volume → extra churn during the manifesto: effective timeSpeed = params.timeSpeed + level × this.
 *  Outside the manifesto the voice level is 0, so this contributes nothing. */
export const MANIFESTO_TIMESPEED_AUDIO_GAIN = 0.1;

/** The completion "explosion": params burst to MANIFESTO_LOOK while motion returns + shake releases.
 *  Fast, front-loaded (power3.out) so the rupture reads as a sudden release of the charged hold. */
export const MANIFESTO_EXPLODE = { duration: 0.6, ease: 'power3.out' };

/** The deactivation "collapse": the manifesto sphere recedes (prismSize 6→0) and the form recomposes
 *  back to the active section preset. Deliberately gentler + slightly longer than the explosion — a
 *  symmetric inertial settle (power2.inOut), so releasing reads as the energy COLLAPSING / recomposing,
 *  not as the same hard burst played backwards or a snap. Used by setManifestoActive(false). */
export const MANIFESTO_COLLAPSE = { duration: 0.9, ease: 'power2.inOut' };

/** Smooth release of freeze+shake when the hold is aborted before completion. */
export const HOLD_RELEASE = { duration: 0.4, ease: 'power2.out' };

/**
 * Shake: a high-frequency camera-origin (uRo) tremble, amplitude = `_shake01 × params.shakeAmp`.
 * `ampDefault` seeds the tunable `shakeAmp` param (live-adjustable in the ?debug panel). Frequencies are
 * rad/s on the real-time clock (_postElapsed, which keeps advancing while the form's own time is frozen).
 * Subtle by intent — raise `shakeAmp` in ?debug if it needs to read more.
 */
export const SHAKE = { ampDefault: 0.1, freqX: 46.25, freqY: 38.75, freqZ: 28.75 };
